import { devicePassesSignalFilter } from './backhaulRssiUtils';
import { nodeDevicesFromHeardSet, resolveCapDevicesForHeardSet } from './heardByMapUtils';

/** Treemgmt entries from list device fields or a fetched configure response. */
export function getDeviceTreemgmtEntries(device, configBySerial) {
  const serial = device?.serial_number?.toLowerCase();
  const fetched = serial && configBySerial?.get?.(serial);
  if (Array.isArray(fetched?.treemgmt)) return fetched.treemgmt;

  const cfg = device?.configuration || device?.Configuration;
  if (Array.isArray(cfg?.treemgmt)) return cfg.treemgmt;
  if (Array.isArray(device?.treemgmt)) return device.treemgmt;
  return [];
}

/** True when any treemgmt entry on the device has max_children === 0. */
export function deviceHasBlockedAPSegmentSlot(device, configBySerial) {
  return getDeviceTreemgmtEntries(device, configBySerial).some((entry) => {
    const raw = entry?.max_children;
    if (raw == null || raw === '') return false;
    const n = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
    return Number.isFinite(n) && n === 0;
  });
}

export function countBlockedAPSegmentSlots(devices, configBySerial) {
  if (!Array.isArray(devices)) return 0;
  return devices.filter((d) => deviceHasBlockedAPSegmentSlot(d, configBySerial)).length;
}

/** True when at least one connected child references this device as parent. */
export function hasConnectedChild(device, catalog) {
  const parentSerial = device?.serial_number?.toLowerCase();
  if (!parentSerial || !Array.isArray(catalog)) return false;
  return catalog.some(
    (d) => d?.connected
      && d.parent_serial
      && d.parent_serial.toLowerCase() === parentSerial,
  );
}

/** Collect descendant serials under `rootDevice` (not including the root). */
export function collectSubtreeDescendantSerials(rootDevice, catalog) {
  const result = new Set();
  const rootSerial = rootDevice?.serial_number?.toLowerCase();
  if (!rootSerial || !Array.isArray(catalog)) return result;

  const walk = (parentSerial) => {
    catalog.forEach((d) => {
      const serial = d?.serial_number?.toLowerCase();
      if (
        serial
        && d.parent_serial
        && d.parent_serial.toLowerCase() === parentSerial
        && !result.has(serial)
      ) {
        result.add(serial);
        walk(serial);
      }
    });
  };

  walk(rootSerial);
  return result;
}

function findDeviceBySerial(catalog, serial) {
  const key = String(serial || '').toLowerCase();
  return catalog.find((d) => d?.serial_number?.toLowerCase() === key) ?? null;
}

/**
 * Build node/CAP lists for a custom-tree segment rooted at `rootDevice`.
 * The root is always included; descendants must pass connected/RSSI filters.
 */
export function buildSubtreeSegmentLists(rootDevice, catalog, opts, getDeviceType) {
  const isCapFn = (d) => getDeviceType(d?.capabilities) === 'CAP';
  if (!rootDevice?.serial_number || !Array.isArray(catalog)) {
    return { members: [], nodes: [], caps: [], descendantCount: 0, includedDescendantCount: 0 };
  }

  const descendantSerials = collectSubtreeDescendantSerials(rootDevice, catalog);
  const members = new Map();
  const rootSerial = rootDevice.serial_number.toLowerCase();
  members.set(rootSerial, rootDevice);

  const signalFilter = opts?.signalFilter || 'all';
  const minRssi = Number(opts?.minRssi);
  const applyRssi = signalFilter !== 'all' && Number.isFinite(minRssi);
  const connectedOnly = Boolean(opts?.connectedOnly);

  let includedDescendantCount = 0;
  descendantSerials.forEach((serial) => {
    const dev = findDeviceBySerial(catalog, serial);
    if (!dev) return;
    if (connectedOnly && !dev.connected) return;
    if (applyRssi && !devicePassesSignalFilter(dev, signalFilter, minRssi, catalog)) return;
    members.set(serial, dev);
    includedDescendantCount += 1;
  });

  const memberList = [...members.values()];
  return {
    members: memberList,
    nodes: nodeDevicesFromHeardSet(memberList, isCapFn),
    caps: resolveCapDevicesForHeardSet(memberList, catalog, isCapFn),
    descendantCount: descendantSerials.size,
    includedDescendantCount,
  };
}
